/**
 * Sound Notification Utility for Web Chat
 * Plays notification sound when new messages arrive
 */

class SoundNotification {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    this.isPlaying = false;
    this.lastPlayTime = 0;
    this.minPlayInterval = 500; // Minimum 500ms between sounds
    this.volume = 0.5; // Default volume (0.0 to 1.0)
    
    // Initialize AudioContext on first user interaction (browser requirement)
    this._initAudioContext();
  }

  /**
   * Initialize AudioContext (required for browser autoplay policies)
   */
  _initAudioContext() {
    // AudioContext can only be created after user interaction
    // We'll create it on first play attempt
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.warn('⚠️ AudioContext not supported:', e);
        this.audioContext = null;
      }
    }
  }
  
  /**
   * Initialize audio on user interaction (click, touch, etc.)
   * Call this on first user interaction to enable audio
   */
  initializeOnUserInteraction() {
    if (!this.audioContext) {
      this._initAudioContext();
    }
    
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume().then(() => {
      }).catch(err => {
        console.warn('⚠️ Could not resume AudioContext:', err);
      });
    }
  }

  /**
   * Check if AudioContext is ready (resumed if suspended)
   */
  async _ensureAudioContext() {
    if (!this.audioContext) {
      this._initAudioContext();
    }
    
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Generate a simple notification beep sound using Web Audio API
   * This works without requiring any audio files
   */
  async _generateBeepSound(frequency = 800, duration = 200, type = 'sine') {
    if (!this.audioContext) {
      return;
    }

    await this._ensureAudioContext();

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    // Create a pleasant notification sound with fade in/out
    const now = this.audioContext.currentTime;
    
    // Fade in
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(this.volume, now + 0.01);
    
    // Fade out
    gainNode.gain.linearRampToValueAtTime(this.volume * 0.7, now + duration / 1000 - 0.05);
    gainNode.gain.linearRampToValueAtTime(0, now + duration / 1000);

    oscillator.start(now);
    oscillator.stop(now + duration / 1000);
  }

  /**
   * Play a double beep notification sound (more noticeable)
   */
  async playNotification() {
    if (!this.isEnabled) {
      return;
    }

    // Initialize AudioContext if needed
    if (!this.audioContext) {
      this._initAudioContext();
      
      // If still null, audio is not supported
      if (!this.audioContext) {
        console.warn('⚠️ AudioContext not available');
        return;
      }
    }

    // Ensure AudioContext is running
    await this._ensureAudioContext();

    // Throttle: Don't play if we played recently
    const now = Date.now();
    if (now - this.lastPlayTime < this.minPlayInterval) {
      return;
    }

    if (this.isPlaying) {
      return;
    }

    this.isPlaying = true;
    this.lastPlayTime = now;

    try {
      // First beep
      await this._generateBeepSound(800, 150, 'sine');
      
      // Small delay
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Second beep (slightly higher pitch)
      await this._generateBeepSound(900, 150, 'sine');
    } catch (error) {
      console.warn('⚠️ Error playing notification sound:', error);
      // If error is due to suspended context, try to resume
      if (this.audioContext && this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
          // Retry once after resume
          if (!this.isPlaying) {
            this.isPlaying = true;
            await this._generateBeepSound(800, 150, 'sine');
            await new Promise(resolve => setTimeout(resolve, 100));
            await this._generateBeepSound(900, 150, 'sine');
          }
        } catch (retryError) {
          console.warn('⚠️ Could not resume AudioContext:', retryError);
        }
      }
    } finally {
      this.isPlaying = false;
    }
  }

  /**
   * Play a single short beep (for typing indicators, etc.)
   */
  async playShortBeep() {
    if (!this.isEnabled) {
      return;
    }

    const now = Date.now();
    if (now - this.lastPlayTime < this.minPlayInterval) {
      return;
    }

    try {
      await this._generateBeepSound(600, 100, 'sine');
      this.lastPlayTime = now;
    } catch (error) {
      console.warn('⚠️ Error playing short beep:', error);
    }
  }

  /**
   * Enable/disable sound notifications
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    // Store preference in localStorage
    try {
      localStorage.setItem('chat_sound_enabled', JSON.stringify(enabled));
    } catch (e) {
      console.warn('⚠️ Could not save sound preference:', e);
    }
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    try {
      localStorage.setItem('chat_sound_volume', JSON.stringify(this.volume));
    } catch (e) {
      console.warn('⚠️ Could not save volume preference:', e);
    }
  }

  /**
   * Load preferences from localStorage
   */
  loadPreferences() {
    try {
      const enabled = localStorage.getItem('chat_sound_enabled');
      if (enabled !== null) {
        this.isEnabled = JSON.parse(enabled);
      }

      const volume = localStorage.getItem('chat_sound_volume');
      if (volume !== null) {
        this.volume = Math.max(0, Math.min(1, JSON.parse(volume)));
      }
    } catch (e) {
      console.warn('⚠️ Could not load sound preferences:', e);
    }
  }

  /**
   * Check if user has interacted with page (required for audio)
   */
  _checkUserInteraction() {
    // AudioContext requires user interaction first
    // This will be triggered on first play attempt
    return true;
  }

  /**
   * Test sound (for settings/preferences)
   */
  async testSound() {
    await this.playNotification();
  }
}

// Export singleton instance
const soundNotification = new SoundNotification();
soundNotification.loadPreferences();

export default soundNotification;

